/**
 * /app/buyers/search — Cash Buyer Discovery (Phase 1 MOCKED).
 *
 * Mirrors the Discover.jsx pattern but searches for cash buyers instead of
 * properties. Returns deterministic fake profiles until ATTOM data is wired.
 *
 * Tabs at top let the user toggle between "Search" (this page) and "My List"
 * (existing /app/buyers).
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Search, Users, Building2, MapPin, TrendingUp, DollarSign,
  Filter, Loader2, AlertCircle, Plus, CheckCircle2, Star,
  Crown, Lock, Briefcase, Calendar, Home, Sparkles,
  Wallet, Gem, Truck, Flame, History, Ruler, Phone,
  AlertTriangle, ShieldAlert, FileWarning, Target,
} from "lucide-react";
import { api, fmtMoney } from "@/lib/api";
import ColdCallScriptModal from "@/components/ColdCallScriptModal";
import BuyerMapView from "@/components/BuyerMapView";

const ENTITY_TYPES = [
  { value: "", label: "Any entity type" },
  { value: "LLC", label: "LLC" },
  { value: "Individual", label: "Individual" },
  { value: "Trust", label: "Trust" },
  { value: "Corporation", label: "Corporation" },
];

const PURCHASED_WITHIN = [
  { value: 0, label: "Any time" },
  { value: 3, label: "Last 3 months" },
  { value: 6, label: "Last 6 months" },
  { value: 12, label: "Last 12 months" },
  { value: 24, label: "Last 24 months" },
];

const ENTITY_ICON = {
  LLC: Briefcase,
  Corporation: Building2,
  Trust: Crown,
  Individual: Users,
};


export default function BuyerSearch() {
  const [filters, setFilters] = useState({
    market: "",
    entity_type: "",
    min_properties: 3,
    min_avg_price: 0,
    max_avg_price: 0,
    purchased_within_months: 12,
  });
  const [results, setResults] = useState([]);
  const [isMocked, setIsMocked] = useState(true);
  const [fallbackReason, setFallbackReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(() => new Set());
  const [searched, setSearched] = useState(false);
  const [scriptBuyer, setScriptBuyer] = useState(null);
  // Tier 4: persist Grid ⇄ Map preference
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("buyerSearch.view") || "grid"; }
    catch { return "grid"; }
  });
  const setViewPersist = (v) => {
    setView(v);
    try { localStorage.setItem("buyerSearch.view", v); } catch { /* ignore */ }
  };
  const handlePinClick = (b) => {
    // Switch back to grid and scroll the matching card into view
    setViewPersist("grid");
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="buyer-card-${b.id}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
      el?.classList.add("ring-2", "ring-emerald-500");
      setTimeout(() => el?.classList.remove("ring-2", "ring-emerald-500"), 2000);
    }, 100);
  };

  const runSearch = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/buyer-search", { params: filters });
      setResults(data.results || []);
      setIsMocked(!!data.is_mocked);
      setFallbackReason(data.fallback_reason || data.empty_reason || "");
      setSearched(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Search failed");
    }
    setLoading(false);
  }, [filters]);

  // Run an initial search on load with default filters so the page isn't empty.
  useEffect(() => {
    const initial = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/buyer-search", { params: filters });
        setResults(data.results || []);
        setIsMocked(!!data.is_mocked);
        setFallbackReason(data.fallback_reason || data.empty_reason || "");
        setSearched(true);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Search failed");
      }
      setLoading(false);
    };
    initial();
  }, []);

  const saveToList = async (buyer) => {
    try {
      const intel = [];
      if (buyer.is_millionaire) intel.push("Millionaire");
      if (buyer.net_worth_label) intel.push(`Net worth ~${buyer.net_worth_label}`);
      if (buyer.income_label) intel.push(`Income ~${buyer.income_label}`);
      if (buyer.recently_moved) intel.push("Recently moved");
      const intelLine = intel.length ? ` Intel: ${intel.join(" · ")}.` : "";
      const payload = {
        name: buyer.name,
        phone: buyer.phone || "",
        email: buyer.email || "",
        market: buyer.primary_market,
        entity_type: buyer.entity_type,
        properties_owned: buyer.properties_owned,
        avg_purchase_price: Math.round((buyer.avg_purchase_price_cents || 0) / 100),
        last_purchase_at: "",
        notes: `Principal: ${buyer.principal_name}. Portfolio: ${buyer.properties_owned} properties (~${fmtMoney(buyer.portfolio_value_cents)}). Buy box: ${buyer.buy_box_label}.${intelLine}`,
      };
      await api.post("/buyer-search/save", payload);
      toast.success(`${buyer.name} added to your buyer list`);
      setSaved((s) => new Set(s).add(buyer.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  // Tier 3: pivot a distressed buyer into a seller lead.
  const dialAsSeller = async (buyer) => {
    if (!buyer.distressed_property_address) {
      toast.error("No property address on file for this distress signal");
      return;
    }
    const flags = (buyer.distress_signals || [])
      .map((d) => d.label || d.key)
      .join(", ");
    try {
      const payload = {
        property_address: buyer.distressed_property_address,
        seller_name: buyer.principal_name || buyer.name,
        stage: "new",
        source: "buyer_search_distress",
        notes: (
          `Pivoted from cash-buyer search. Owner: ${buyer.name}` +
          (buyer.principal_name ? ` (${buyer.principal_name})` : "") +
          `. Distress signals: ${flags}.` +
          ` Owner's portfolio: ${buyer.properties_owned} properties in ${buyer.primary_market}.` +
          (buyer.net_worth_label ? ` Net worth ~${buyer.net_worth_label}.` : "")
        ),
      };
      const r = await api.post("/leads", payload);
      const leadId = r?.data?.id || r?.data?.lead_id;
      toast.success("Seller lead created — opening it now");
      if (leadId) {
        setTimeout(() => { window.location.href = `/app/leads/${leadId}`; }, 600);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not create lead";
      if (e?.response?.status === 402) {
        toast.error(msg + " · Upgrade to Pro for unlimited leads.");
      } else {
        toast.error(msg);
      }
    }
  };

  return (
    <div className="space-y-6" data-testid="buyer-search-page">
      {/* Header */}
      <div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">Buyer Discovery — Phase 1</div>
        <h1 className="font-display font-black text-3xl tracking-tighter">Search for cash buyers</h1>
        <p className="text-[var(--ink-2)] mt-1 max-w-2xl">
          Find serious investors actively buying in your market. Filter by portfolio size,
          entity type, and recent activity. Save anyone interesting to your buyer list.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[var(--line)]">
        <button
          type="button"
          className="px-4 py-2 text-sm font-bold border-b-2 -mb-px border-emerald-500 text-emerald-700"
          data-testid="tab-search"
        >
          <Search size={14} className="inline -mt-0.5 mr-1" /> Search
        </button>
        <Link
          to="/app/buyers"
          className="px-4 py-2 text-sm font-bold border-b-2 -mb-px border-transparent text-[var(--ink-3)] hover:text-[var(--ink)]"
          data-testid="tab-my-list"
        >
          <Users size={14} className="inline -mt-0.5 mr-1" /> My Buyer List
        </Link>
      </div>

      {/* Source/data-quality banner */}
      {isMocked ? (
        <div className="card border-l-4 border-l-amber-400 bg-amber-50/50 p-3 text-sm flex items-start gap-2" data-testid="mock-banner">
          <AlertCircle size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <div>
            <strong className="text-amber-900">Sample data</strong>
            <span className="text-amber-800">
              {" "}— {fallbackReason || "BatchData Property Search not enabled on your plan."} Showing mock profiles so you can preview the UX.
            </span>
          </div>
        </div>
      ) : (
        <div className="card border-l-4 border-l-emerald-400 bg-emerald-50/50 p-3 text-sm flex items-start gap-2" data-testid="live-banner">
          <CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <strong className="text-emerald-900">Live data</strong>
            <span className="text-emerald-800">
              {" "}— results pulled from BatchData (155M+ properties). Cached 60 min per filter combo to keep your spend low.
            </span>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="card p-4" data-testid="filter-bar">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <Field label="Market (city, ZIP, or 'City, ST')">
            <input
              value={filters.market}
              onChange={(e) => setFilters((f) => ({ ...f, market: e.target.value }))}
              placeholder="e.g. Dallas, TX  or  75201"
              className="input"
              data-testid="filter-market"
            />
          </Field>
          <Field label="Entity type">
            <select
              value={filters.entity_type}
              onChange={(e) => setFilters((f) => ({ ...f, entity_type: e.target.value }))}
              className="input"
              data-testid="filter-entity"
            >
              {ENTITY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </Field>
          <Field label="Purchased within">
            <select
              value={filters.purchased_within_months}
              onChange={(e) => setFilters((f) => ({ ...f, purchased_within_months: Number(e.target.value) }))}
              className="input"
              data-testid="filter-purchased"
            >
              {PURCHASED_WITHIN.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
          </Field>
          <Field label="Min properties owned">
            <input
              type="number" min="0" max="1000"
              value={filters.min_properties}
              onChange={(e) => setFilters((f) => ({ ...f, min_properties: Number(e.target.value) || 0 }))}
              className="input"
              data-testid="filter-min-props"
            />
          </Field>
          <Field label="Min avg purchase ($)">
            <input
              type="number" min="0"
              value={filters.min_avg_price}
              onChange={(e) => setFilters((f) => ({ ...f, min_avg_price: Number(e.target.value) || 0 }))}
              placeholder="0 = no min"
              className="input"
              data-testid="filter-min-avg"
            />
          </Field>
          <Field label="Max avg purchase ($)">
            <input
              type="number" min="0"
              value={filters.max_avg_price}
              onChange={(e) => setFilters((f) => ({ ...f, max_avg_price: Number(e.target.value) || 0 }))}
              placeholder="0 = no cap"
              className="input"
              data-testid="filter-max-avg"
            />
          </Field>
        </div>
        <div className="flex items-center justify-end gap-2 mt-3">
          <button
            type="button"
            className="btn-outline"
            onClick={() => setFilters({
              market: "", entity_type: "", min_properties: 0,
              min_avg_price: 0, max_avg_price: 0, purchased_within_months: 0,
            })}
            data-testid="filter-reset"
          >
            Reset
          </button>
          <button
            type="button"
            className="btn-money"
            onClick={runSearch}
            disabled={loading}
            data-testid="filter-search"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            Search
          </button>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="text-center py-12 text-[var(--ink-3)]" data-testid="results-loading">
          <Loader2 size={20} className="animate-spin mx-auto mb-2" /> Searching…
        </div>
      ) : !searched ? null : results.length === 0 ? (
        <div className="card text-center py-16" data-testid="results-empty">
          <Search size={32} className="mx-auto text-[var(--ink-3)] opacity-40 mb-2" />
          <p className="text-[var(--ink-2)]">No buyers match those filters. Loosen the criteria and try again.</p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between text-sm" data-testid="results-summary">
            <span className="text-[var(--ink-3)]">
              <strong className="text-[var(--ink)]">{results.length}</strong> cash buyer{results.length === 1 ? "" : "s"} matching
            </span>
            <div className="flex items-center gap-3">
              <span className="text-[10px] uppercase tracking-wider font-bold text-[var(--ink-3)]">
                Sorted by sale propensity
              </span>
              {/* Tier 4: Grid ⇄ Map toggle */}
              <div
                className="inline-flex items-center rounded-lg border border-[var(--line)] bg-[var(--bg-1)] p-0.5"
                data-testid="buyer-view-toggle"
              >
                <button
                  type="button"
                  onClick={() => setViewPersist("grid")}
                  className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md transition-colors ${
                    view === "grid"
                      ? "bg-[var(--ink)] text-white"
                      : "text-[var(--ink-3)] hover:text-[var(--ink)]"
                  }`}
                  data-testid="buyer-view-grid"
                >
                  Grid
                </button>
                <button
                  type="button"
                  onClick={() => setViewPersist("map")}
                  className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md transition-colors ${
                    view === "map"
                      ? "bg-[var(--ink)] text-white"
                      : "text-[var(--ink-3)] hover:text-[var(--ink)]"
                  }`}
                  data-testid="buyer-view-map"
                >
                  <MapPin size={10} className="inline -mt-0.5 mr-0.5" /> Map
                </button>
              </div>
            </div>
          </div>

          {view === "map" ? (
            <BuyerMapView buyers={results} onPinClick={handlePinClick} />
          ) : (
            <div className="grid lg:grid-cols-2 gap-4">
              {results.map((b) => (
                <BuyerResultCard
                  key={b.id}
                  buyer={b}
                  saved={saved.has(b.id)}
                  onSave={() => saveToList(b)}
                  onScript={() => setScriptBuyer(b)}
                  onDialSeller={() => dialAsSeller(b)}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* AI Cold-Call Script modal */}
      <ColdCallScriptModal
        open={!!scriptBuyer}
        buyer={scriptBuyer}
        onClose={() => setScriptBuyer(null)}
      />
    </div>
  );
}


function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider font-bold text-[var(--ink-3)]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}


function BuyerResultCard({ buyer, saved, onSave, onScript, onDialSeller }) {
  const Icon = ENTITY_ICON[buyer.entity_type] || Users;
  const propensity = buyer.sale_propensity_score ?? buyer.activity_score;
  const scoreColor =
    propensity >= 80 ? "text-emerald-700 bg-emerald-50 border-emerald-200"
    : propensity >= 60 ? "text-amber-700 bg-amber-50 border-amber-200"
    : "text-[var(--ink-3)] bg-[var(--bg-2)] border-[var(--line)]";
  const scoreLabel =
    propensity >= 80 ? "Hot"
    : propensity >= 60 ? "Warm"
    : "Cold";
  const hasIntel = buyer.net_worth_label || buyer.income_label
                   || buyer.is_millionaire || buyer.recently_moved;

  return (
    <div className="card p-5 hover:shadow-md transition-shadow" data-testid={`buyer-card-${buyer.id}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="rounded-lg bg-[var(--bg-2)] p-1.5 shrink-0">
              <Icon size={18} className="text-[var(--ink-2)]" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="font-display font-bold text-lg truncate flex items-center gap-1.5">
                {buyer.name}
                {buyer.is_millionaire && (
                  <span
                    title="Estimated net worth ≥ $1M"
                    className="inline-flex items-center gap-0.5 text-[9px] uppercase font-bold tracking-wider px-1.5 py-0.5 rounded bg-gradient-to-r from-amber-400 to-yellow-500 text-white shrink-0"
                    data-testid={`buyer-millionaire-${buyer.id}`}
                  >
                    <Gem size={9} /> Millionaire
                  </span>
                )}
              </h3>
              <div className="text-xs text-[var(--ink-3)] truncate">
                {buyer.entity_type !== "Individual" && (
                  <span>Principal: <strong className="text-[var(--ink-2)]">{buyer.principal_name}</strong></span>
                )}
              </div>
            </div>
          </div>
        </div>
        <div
          className={`text-[10px] uppercase font-bold tracking-wider px-2 py-1 rounded border shrink-0 ${scoreColor}`}
          title="BatchData sale-propensity score (0–99). Higher = more likely to transact."
          data-testid={`buyer-propensity-${buyer.id}`}
        >
          <Flame size={9} className="inline -mt-0.5 mr-0.5" /> {scoreLabel} · {propensity}
        </div>
      </div>

      {/* Wealth signals row */}
      {hasIntel && (
        <div
          className="flex items-center flex-wrap gap-1.5 mb-3"
          data-testid={`buyer-intel-${buyer.id}`}
        >
          {buyer.net_worth_label && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200"
              title="Estimated household net worth"
            >
              <Wallet size={10} /> Net worth {buyer.net_worth_label}
            </span>
          )}
          {buyer.income_label && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-sky-50 text-sky-800 border border-sky-200"
              title="Estimated annual household income"
            >
              <DollarSign size={10} /> Income {buyer.income_label}
            </span>
          )}
          {buyer.recently_moved && (
            <span
              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-violet-50 text-violet-800 border border-violet-200"
              title="Buyer has moved in the last 12 months — often actively investing"
              data-testid={`buyer-recently-moved-${buyer.id}`}
            >
              <Truck size={10} /> Recently moved
            </span>
          )}
        </div>
      )}

      {/* Markets */}
      <div className="text-xs mb-3">
        <div className="flex items-center gap-1 text-[var(--ink-2)] mb-0.5">
          <MapPin size={11} /> <strong>{buyer.primary_market}</strong>
        </div>
        {buyer.secondary_markets?.length > 0 && (
          <div className="text-[10px] text-[var(--ink-3)] ml-4">
            Also buys in: {buyer.secondary_markets.join(", ")}
          </div>
        )}
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-3 gap-2 mb-3 pt-3 border-t border-[var(--line)]">
        <Stat icon={Home} value={buyer.properties_owned} label="Properties" />
        <Stat icon={DollarSign} value={`$${Math.round((buyer.avg_purchase_price_cents || 0) / 100 / 1000)}k`} label="Avg buy" />
        <Stat icon={TrendingUp} value={fmtMoney(buyer.portfolio_value_cents)} label="Portfolio" />
      </div>

      {/* Buy box */}
      <div className="text-xs px-3 py-2 rounded-lg bg-emerald-50/60 mb-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-0.5">
          Likely buy box
        </div>
        <div className="text-emerald-900">{buyer.buy_box_label}</div>
        {(buyer.avg_beds || buyer.avg_sqft || buyer.year_built) && (
          <div
            className="text-[10px] text-emerald-800/80 mt-1 flex items-center gap-1 flex-wrap"
            data-testid={`buyer-building-${buyer.id}`}
          >
            <Ruler size={9} />
            {buyer.avg_beds ? `${buyer.avg_beds}BR` : ""}
            {buyer.avg_baths ? ` · ${Number(buyer.avg_baths).toFixed(buyer.avg_baths % 1 === 0 ? 0 : 1)}BA` : ""}
            {buyer.avg_sqft ? ` · ${Number(buyer.avg_sqft).toLocaleString()} sqft` : ""}
            {buyer.year_built ? ` · built ${buyer.year_built}` : ""}
          </div>
        )}
      </div>

      {/* Deed history strip */}
      {buyer.deed_history?.count > 1 && (
        <div
          className="text-xs px-3 py-2 rounded-lg bg-indigo-50/60 border border-indigo-100 mb-3"
          data-testid={`buyer-deed-history-${buyer.id}`}
        >
          <div className="text-[10px] font-bold uppercase tracking-wider text-indigo-700 mb-0.5 flex items-center gap-1">
            <History size={10} /> Deed history
          </div>
          <div className="text-indigo-900 leading-snug">
            <strong>{buyer.deed_history.count}</strong> recorded sale{buyer.deed_history.count === 1 ? "" : "s"}
            {buyer.deed_history.last_sale_date && (
              <> · last <strong>{buyer.deed_history.last_sale_date.slice(0, 7)}</strong></>
            )}
            {buyer.flip_count > 0 && (
              <> · <strong className="text-amber-700">{buyer.flip_count}</strong> flip{buyer.flip_count === 1 ? "" : "s"} (&lt;12mo holds)</>
            )}
            {buyer.avg_hold_months && (
              <> · avg hold <strong>{buyer.avg_hold_months}mo</strong></>
            )}
          </div>
          {buyer.deed_history.last_sale_price_cents > 0 && buyer.deed_history.prior_sale_price_cents > 0 && (
            <div className="text-[10px] text-indigo-800/70 mt-0.5">
              Last:{" "}
              <strong>${Math.round(buyer.deed_history.prior_sale_price_cents / 100_000) * 100}k</strong>
              {" → "}
              <strong>${Math.round(buyer.deed_history.last_sale_price_cents / 100_000) * 100}k</strong>
            </div>
          )}
        </div>
      )}

      {/* Tier 3: Distress signals + seller-pivot opportunity */}
      {buyer.has_distress && buyer.distress_signals?.length > 0 && (
        <div
          className="text-xs px-3 py-2.5 rounded-lg bg-rose-50/70 border border-rose-200 mb-3"
          data-testid={`buyer-distress-${buyer.id}`}
        >
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700 flex items-center gap-1">
              <ShieldAlert size={10} /> Distress · seller opportunity
            </div>
            {buyer.distressed_property_address && (
              <button
                type="button"
                onClick={onDialSeller}
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-rose-600 text-white hover:bg-rose-700 transition-colors"
                data-testid={`buyer-dial-seller-${buyer.id}`}
                title="Create a seller lead from this distressed property"
              >
                <Target size={9} /> Dial as seller →
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1 mb-1">
            {buyer.distress_signals.map((d) => {
              const tone =
                d.severity === "high" ? "bg-rose-100 text-rose-900 border-rose-300"
                : d.severity === "med" ? "bg-amber-100 text-amber-900 border-amber-300"
                : "bg-slate-100 text-slate-800 border-slate-200";
              const Icon =
                d.severity === "high" ? AlertTriangle
                : d.severity === "med" ? FileWarning
                : AlertCircle;
              return (
                <span
                  key={d.key}
                  className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md border ${tone}`}
                  title={d.hint || ""}
                >
                  <Icon size={9} /> {d.label}
                </span>
              );
            })}
          </div>
          {buyer.distressed_property_address && (
            <div className="text-[10px] text-rose-800/70 italic">
              On: <strong className="text-rose-900 not-italic">{buyer.distressed_property_address}</strong>
            </div>
          )}
        </div>
      )}

      {/* Last purchase */}
      <div className="text-xs flex items-center gap-1 text-[var(--ink-3)] mb-4">
        <Calendar size={11} />
        Last cash purchase: <strong className="text-[var(--ink-2)]">{buyer.last_purchase_months_ago}</strong> month{buyer.last_purchase_months_ago === 1 ? "" : "s"} ago
      </div>

      {/* Locked contact + Save action */}
      <div className="flex items-center justify-between gap-2">
        <div className="text-[10px] text-[var(--ink-3)] flex items-center gap-1">
          <Lock size={10} /> Contact via skip-trace
        </div>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            className="btn-outline text-xs"
            onClick={onScript}
            title="Generate a personalized cold-call script with Claude"
            data-testid={`buyer-script-${buyer.id}`}
          >
            <Phone size={12} /> Script
          </button>
          {saved ? (
            <button
              type="button"
              className="btn-outline text-xs text-emerald-700 border-emerald-300 bg-emerald-50"
              disabled
              data-testid={`buyer-saved-${buyer.id}`}
            >
              <CheckCircle2 size={12} /> Saved
            </button>
          ) : (
            <button
              type="button"
              className="btn-money text-xs"
              onClick={onSave}
              data-testid={`buyer-save-${buyer.id}`}
            >
              <Plus size={12} /> Save
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function Stat({ icon: Icon, value, label }) {
  return (
    <div className="text-center">
      <div className="flex items-center justify-center gap-1 text-[var(--ink)]">
        <Icon size={12} className="text-[var(--ink-3)]" />
        <span className="font-display font-bold text-sm">{value}</span>
      </div>
      <div className="text-[9px] uppercase tracking-wider text-[var(--ink-3)] mt-0.5">{label}</div>
    </div>
  );
}
